import React, { useEffect, useState } from "react";
import { supabase } from "../SupabaseClient.tsx";
import { useNavigate } from "react-router-dom";
import ActionButton from "../components/ActionButton.tsx";
import "./UpdatePassword.css";
import { PiProhibit } from "react-icons/pi";

const UpdatePassword = () => {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isEmailProvider, setIsEmailProvider] = useState<boolean>(false);
  const [socialProvider, setSocialProvider] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [passwordUpdateStatus, setPasswordUpdateStatus] = useState<{
    message: string;
    isError: boolean;
  } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [isPasswordRecoveryFlow, setIsPasswordRecoveryFlow] =
    useState<boolean>(false);

  // Password requirements state
  const [passwordRequirements, setPasswordRequirements] = useState({
    hasLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSymbol: false,
  });

  // Check if all requirements are met for the update button
  const allRequirementsMet = Object.values(passwordRequirements).every(
    (req) => req === true
  );

  // Helper function to check password requirements
  const checkPasswordRequirements = (password: string) => {
    setPasswordRequirements({
      hasLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSymbol: /[#?!@$%^&*]/.test(password),
    });
  };

  // Check user authentication on load
  useEffect(() => {
    const checkUserAuth = async () => {
      try {
        setInitialLoading(true);
        const { data: authData } = await supabase.auth.getUser();

        if (authData && authData.user) {
          setUserEmail(authData.user.email ?? null);

          // Check if the user authenticated with email provider
          const provider = authData.user.app_metadata?.provider;
          setIsEmailProvider(provider === "email" || !provider);

          // Store the social provider name if not email
          if (provider && provider !== "email") {
            setSocialProvider(provider);
          }

          // Check if this is a password recovery flow (user came from reset link)
          // Recovery tokens in URL indicate password recovery
          const hash = window.location.hash;
          const isRecovery =
            hash.includes("type=recovery") ||
            window.location.pathname === "/update-password";
          setIsPasswordRecoveryFlow(isRecovery);
        } else {
          // If no user, redirect to landing page
          navigate("/");
        }
      } catch (error) {
        console.error("Error checking authentication:", error);
        navigate("/");
      } finally {
        setInitialLoading(false);
      }
    };

    checkUserAuth();
  }, [navigate]);

  // Handle password updates
  const handleUpdatePassword = async () => {
    setLoading(true);
    setPasswordUpdateStatus(null);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        throw error;
      }

      setPasswordUpdateStatus({
        message: "Password updated successfully! Signing you out...",
        isError: false,
      });

      // Wait for 2 seconds to show success message before signing out
      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate("/");
      }, 2000);
    } catch (error: any) {
      console.error("Error updating password:", error);
      setPasswordUpdateStatus({
        message: error.message || "Failed to update password",
        isError: true,
      });
      setLoading(false);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setNewPassword(password);
    checkPasswordRequirements(password);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleCancel = async () => {
    // If in password recovery flow, sign out and go to landing page
    if (isPasswordRecoveryFlow) {
      await supabase.auth.signOut();
      navigate("/");
    } else {
      navigate("/settings");
    }
  };

  if (initialLoading) {
    return (
      <div className="update-password-page">
        <div className="update-password-container">
          <h1>Loading...</h1>
        </div>
      </div>
    );
  }

  // If user is using social login, show a message
  if (!isEmailProvider) {
    return (
      <div className="update-password-page">
        <div className="update-password-container">
          <div className="update-password-header">
            <h1>Update Password</h1>
          </div>
          <div className="social-login-notice">
            <h2>Social Login Detected</h2>
            <p>
              You're signed in with {socialProvider || "a social provider"}.
              Password management is handled by your social login provider.
            </p>
            <ActionButton
              text={
                isPasswordRecoveryFlow ? "Back to Sign In" : "Back to Settings"
              }
              onClick={handleCancel}
              secondary={false}
              responsive={false}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="update-password-page">
      <div className="update-password-container">
        <div className="update-password-header">
          <h1>Update Password</h1>
          <p>Please create a new password for your account.</p>
        </div>

        {passwordUpdateStatus && (
          <div
            className={`update-status ${
              passwordUpdateStatus.isError ? "error" : "success"
            }`}
          >
            {passwordUpdateStatus.message}
          </div>
        )}

        <div className="password-input-wrapper">
          <div className="email-display">
            <strong>Account:</strong> {userEmail}
          </div>
          <div className="password-input-container">
            <input
              type={showPassword ? "text" : "password"}
              value={newPassword}
              onChange={handlePasswordChange}
              placeholder="Enter new password"
              disabled={loading}
            />
            <button
              type="button"
              className="toggle-password-visibility"
              onClick={togglePasswordVisibility}
              disabled={loading}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>

          <div className="password-requirements">
            <div
              className={`requirement ${
                passwordRequirements.hasLength ? "met" : ""
              }`}
            >
              <span className="indicator">
                {passwordRequirements.hasLength ? "✓" : "○"}
              </span>
              At least 8 characters
            </div>
            <div
              className={`requirement ${
                passwordRequirements.hasUppercase ? "met" : ""
              }`}
            >
              <span className="indicator">
                {passwordRequirements.hasUppercase ? "✓" : "○"}
              </span>
              At least one uppercase letter
            </div>
            <div
              className={`requirement ${
                passwordRequirements.hasLowercase ? "met" : ""
              }`}
            >
              <span className="indicator">
                {passwordRequirements.hasLowercase ? "✓" : "○"}
              </span>
              At least one lowercase letter
            </div>
            <div
              className={`requirement ${
                passwordRequirements.hasNumber ? "met" : ""
              }`}
            >
              <span className="indicator">
                {passwordRequirements.hasNumber ? "✓" : "○"}
              </span>
              At least one number
            </div>
            <div
              className={`requirement ${
                passwordRequirements.hasSymbol ? "met" : ""
              }`}
            >
              <span className="indicator">
                {passwordRequirements.hasSymbol ? "✓" : "○"}
              </span>
              At least one special character (#?!@$%^&*)
            </div>
          </div>

          <div className="update-password-actions">
            <ActionButton
              text={loading ? "Updating..." : "Update Password"}
              onClick={handleUpdatePassword}
              disabled={!allRequirementsMet || loading}
              secondary={false}
              responsive={false}
            />
            <ActionButton
              text={isPasswordRecoveryFlow ? "Back to Sign In" : "Cancel"}
              onClick={handleCancel}
              secondary={true}
              responsive={true}
              disabled={loading}
              icon={<PiProhibit size={18} />}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdatePassword;
